<template>
  <div class="privacy-policy py-4">
    <p class="pp-brand-name">MY LIFE CLICK</p>
    <p class="pp-brand-sub"><em>a SIGABRAD solution</em></p>
    <p class="pp-brand-doc"><em>CNPJ: 44.360.218/0001-22</em></p>

    <h1 class="pp-title">Privacy Policy, Data Protection and Terms of Use</h1>
    <p class="pp-legal-basis">
      <em
        >In accordance with Law No. 13,709/2018 (Brazilian General Data
        Protection Law – LGPD), the Brazilian Civil Rights Framework for the
        Internet (Law No. 12,965/2014), the Consumer Protection Code (Law No.
        8,078/1990) and the current guidelines of Brazil's National Data
        Protection Authority (ANPD)</em
      >
    </p>
    <p class="pp-meta">
      Updated version — August 25, 2026 · Fully replaces the August 2021 version
    </p>

    <h2 class="pp-section">
      1. Nature of this Document and Responsibility for Completion
    </h2>
    <p>
      This is a template document, structured by My Life Click / SIGABRAD for
      use by its purchasing clients — hospitals, clinics, educational
      institutions, event organizers, companies and other legal entities that
      license the platform to identify their own data subjects (patients,
      students, employees, participants, etc.).
    </p>
    <p>
      All fields indicated as "[to be completed by the Purchaser]" throughout
      this document are the exclusive responsibility of the Purchaser of My Life
      Click products, who must complete them, validate them with their own legal
      counsel, and publish them before making the service available to their end
      data subjects. My Life Click does not complete, validate, or assume
      responsibility for the content of these fields.
    </p>

    <h2 class="pp-section">
      2. Identification of Parties and Roles (Controller x Processor)
    </h2>
    <p>
      Under Art. 5, VI and VII, of the LGPD, two roles are distinguished in this
      relationship:
    </p>
    <ul class="pp-list">
      <li>
        My Life Click / SIGABRAD generally acts as Processor — providing digital
        identification technology (QR Code, NFC and RFID), the platform
        infrastructure and, when engaged to do so, support for data processing
        on behalf of and under instructions from the Purchaser.
      </li>
      <li>
        Purchaser of My Life Click products: the legal entity that contracts the
        platform and makes it available to its own data subjects. As a rule, the
        Purchaser acts as Controller before these end data subjects, being
        responsible for decisions on the purpose and manner of processing data
        collected through the wristbands, cards and other devices provided to
        its users.
      </li>
      <li>
        If in doubt, contact us by email at
        <a href="mailto:yusefwehbe@gmail.com">yusefwehbe@gmail.com</a>
      </li>
      <li>
        <strong>Identification of the Purchaser/Controller: </strong>
        <em class="pp-placeholder"
          >[to be completed by the Purchaser — legal name, tax ID, address and
          contact details]</em
        >
      </li>
    </ul>
    <p class="pp-note">
      <em
        >Note: in specific deployments in which My Life Click operates directly
        on a B2C basis (a direct relationship with the end consumer, without an
        intermediary institutional Purchaser), My Life Click assumes the role of
        Controller and must complete these same fields with its own
        information.</em
      >
    </p>
    <p>
      The Controller identified above is responsible for decisions regarding the
      processing of personal data collected through the website, wristbands,
      cards and other identification devices using QR Code, NFC and RFID
      technology, applications and service channels associated with its
      deployment of the platform.
    </p>

    <h2 class="pp-section">3. Definitions</h2>
    <ul class="pp-list">
      <li>
        <strong>Personal data:</strong> information relating to an identified or
        identifiable natural person.
      </li>
      <li>
        <strong>Sensitive personal data:</strong> data concerning health, ethnic
        origin, religious belief, political opinion, genetic or biometric data,
        when linked to a natural person.
      </li>
      <li>
        <strong>Data subject:</strong> the natural person to whom the personal
        data being processed relates.
      </li>
      <li>
        <strong>Processing:</strong> any operation carried out with personal
        data, such as collection, use, access, storage, deletion and sharing.
      </li>
      <li>
        <strong>Controller:</strong> the legal entity responsible for decisions
        regarding the processing of personal data.
      </li>
      <li>
        <strong>Processor:</strong> the legal entity that processes personal
        data on behalf of the Controller.
      </li>
      <li>
        <strong>Data Protection Officer (DPO):</strong> the person appointed to
        act as a communication channel between the Controller, data subjects and
        the ANPD.
      </li>
      <li>
        <strong>ANPD:</strong> Brazilian National Data Protection Authority.
      </li>
    </ul>

    <h2 class="pp-section">4. Personal Data Collected</h2>
    <p>
      Due to the multi-vertical nature of the platform — identification of data
      subjects with chronic diseases, Autism Spectrum Disorder and ADHD (under
      the PIPTEA program, pursuant to Federal Law No. 13,977/2020), rare
      diseases, pet identification, corporate security and large events — My
      Life Click may process the following categories of data, always limited to
      the minimum necessary for each purpose (principle of necessity, Art. 6,
      III):
    </p>
    <h3 class="pp-subsection">4.1. Registration data</h3>
    <ul class="pp-list">
      <li>
        Full name, email, mobile phone, access password and preferred language.
      </li>
      <li>
        Legal guardian's data, when the data subject is a child, adolescent or
        person with a disability under guardianship or representation.
      </li>
    </ul>
    <h3 class="pp-subsection">
      4.2. Sensitive health data (when voluntarily provided by the data subject
      or their guardian)
    </h3>
    <ul class="pp-list">
      <li>
        Medical conditions, diagnoses, allergies, medications in use and
        emergency contacts, linked to the identification wristband or card.
      </li>
      <li>
        Information relating to an ASD/ADHD diagnosis, solely for the purpose of
        identifying and protecting the data subject, pursuant to Law No.
        13,977/2020 (Romeo Mion Law / PIPTEA).
      </li>
    </ul>
    <h3 class="pp-subsection">4.3. Browsing data</h3>
    <ul class="pp-list">
      <li>
        IP address, device type, cookies and session identifiers, collected in
        accordance with the Cookie Policy (Section 13).
      </li>
    </ul>
    <h3 class="pp-subsection">4.4. Data of linked third parties</h3>
    <ul class="pp-list">
      <li>
        When the data subject registers data of dependents, pets or persons
        under their responsibility, the data subject is responsible for
        obtaining consent or having the applicable legal basis for registering
        such data.
      </li>
    </ul>

    <h2 class="pp-section">5. Purpose and Legal Basis for Processing</h2>
    <p>
      Each processing purpose is associated with a specific legal basis provided
      for in Arts. 7 and 11 of the LGPD, as detailed below. The processing of
      sensitive health data observes additional rigor, being carried out only
      with the specific, highlighted and informed consent of the data subject or
      their legal guardian.
    </p>
    <div class="pp-table-wrap">
      <table class="pp-table">
        <thead>
          <tr>
            <th>Purpose of processing</th>
            <th>Legal basis (LGPD)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Registration and account creation</td>
            <td>
              Performance of a contract / preliminary procedures (Art. 7, V)
            </td>
          </tr>
          <tr>
            <td>
              Issuance and linking of the identification wristband/card (QR
              Code, NFC, RFID)
            </td>
            <td>
              Performance of a contract and, where applicable, protection of the
              life or physical safety of the data subject (Art. 7, VII)
            </td>
          </tr>
          <tr>
            <td>
              Health data linked to chronic diseases, ASD/ADHD (PIPTEA) and rare
              diseases
            </td>
            <td>
              Specific and highlighted consent of the data subject or their
              legal guardian, for health protection purposes, in a procedure
              carried out exclusively by health professionals or health entities
              (Art. 11, II, "f")
            </td>
          </tr>
          <tr>
            <td>Communications, offers and promotions by email</td>
            <td>Consent (opt-in), revocable at any time</td>
          </tr>
          <tr>
            <td>Usage metrics, analytics and non-essential cookies</td>
            <td>Consent, according to preferences set by the data subject</td>
          </tr>
          <tr>
            <td>Compliance with a legal or regulatory obligation</td>
            <td>
              Legal or regulatory obligation of the Controller (Art. 7, II)
            </td>
          </tr>
          <tr>
            <td>Fraud prevention and data subject security</td>
            <td>
              Legitimate interest of the Controller, with impact assessment
              (Art. 7, IX)
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="pp-note">
      <em
        >Note: consent for sensitive data is always requested in a highlighted
        manner, separate from the other terms of use, for a specific and
        determined purpose, pursuant to Art. 11, I, of the LGPD.</em
      >
    </p>

    <h2 class="pp-section">6. Consent</h2>
    <p>
      Consent is freely given, informed and unambiguous, collected through an
      active opt-in mechanism (not pre-checked), at the time of registration.
      The data subject may withdraw consent at any time, by request to the Data
      Protection Officer, without prejudice to the lawfulness of processing
      carried out prior to withdrawal.
    </p>
    <p>
      For data of children and adolescents, consent is obtained specifically
      from the legal guardian, observing the best interests of the child,
      pursuant to Art. 14 of the LGPD and the Brazilian Child and Adolescent
      Statute.
    </p>

    <h2 class="pp-section">7. Data Sharing</h2>
    <p>
      My Life Click does not share personal data with third parties for
      commercial purposes unrelated to the provision of the service. Sharing
      occurs exclusively in the following cases:
    </p>
    <ul class="pp-list">
      <li>
        With processors engaged for hosting, payment processing and technical
        support, under a contract ensuring an equivalent standard of protection.
      </li>
      <li>
        With health professionals, emergency teams or public entities, at the
        time the QR Code/NFC is read, for purposes of protecting the life and
        physical safety of the data subject.
      </li>
      <li>
        With institutional partners (hospitals, clinics, rare disease support
        entities, educational institutions), exclusively under a legal basis
        and/or the data subject's specific consent.
      </li>
      <li>
        By legal requirement, court order or request from a competent authority.
      </li>
    </ul>
    <p>
      The Controller requires all processors and partners to comply with
      obligations equivalent to those set out in the LGPD, formalized in a
      contract or data processing agreement (DPA).
    </p>

    <h2 class="pp-section">8. International Data Transfer</h2>
    <p>
      Should personal data be transferred to servers or service providers
      located outside Brazilian national territory, such transfer will comply
      with one of the cases set out in Art. 33 of the LGPD, including the
      existence of an adequacy decision by the ANPD, standard contractual
      clauses, global binding corporate rules, or recognized seals and
      certifications, ensuring an adequate level of protection for the
      transferred data.
    </p>

    <h2 class="pp-section">9. Data Retention and Deletion</h2>
    <p>
      Personal data is retained only for the period necessary to fulfill the
      purposes for which it was collected, observing the mandatory retention
      periods provided by law (tax, civil and regulatory). Once the purpose has
      been fulfilled, the data is deleted or anonymized, except in the cases set
      out in Art. 16 of the LGPD (compliance with a legal obligation, study by a
      research body, transfer to a third party respecting processing
      requirements, or exclusive use by the Controller, with third-party access
      prohibited, provided the data is anonymized).
    </p>

    <h2 class="pp-section">10. Information Security</h2>
    <p>
      The Controller adopts technical and administrative measures suited to
      protecting personal data from unauthorized access and from accidental or
      unlawful destruction, loss, alteration, communication or disclosure,
      including: encryption of sensitive data in transit and at rest, role-based
      access control (least privilege), multi-factor authentication for
      administrative access, access logging and audit trails, and periodic
      security testing.
    </p>
    <p>
      In the event of a security incident that may cause relevant risk or harm
      to data subjects, the Controller will notify the ANPD and the affected
      data subjects within a reasonable period, pursuant to Art. 48 of the LGPD,
      informing the nature of the affected data, the technical measures adopted
      and the risks related to the incident.
    </p>

    <h2 class="pp-section">11. Data Subject Rights</h2>
    <p>
      Under Art. 18 of the LGPD, the data subject has the right to obtain from
      the Controller, at any time and upon request:
    </p>
    <div class="pp-table-wrap">
      <table class="pp-table pp-table--rights">
        <tbody>
          <tr>
            <th>Confirmation and access</th>
            <td>
              To know whether their data is processed and to access the
              processed content.
            </td>
          </tr>
          <tr>
            <th>Correction</th>
            <td>To correct incomplete, inaccurate or outdated data.</td>
          </tr>
          <tr>
            <th>Anonymization, blocking or deletion</th>
            <td>
              Of unnecessary or excessive data, or data processed in
              non-compliance with the LGPD.
            </td>
          </tr>
          <tr>
            <th>Portability</th>
            <td>
              To request the transfer of data to another service provider, upon
              express request.
            </td>
          </tr>
          <tr>
            <th>Deletion</th>
            <td>
              Deletion of personal data processed based on consent, except in
              cases of mandatory retention.
            </td>
          </tr>
          <tr>
            <th>Information about sharing</th>
            <td>
              To know which public and private entities the Controller has
              shared data with.
            </td>
          </tr>
          <tr>
            <th>Information on the possibility of not consenting</th>
            <td>And the consequences of refusal.</td>
          </tr>
          <tr>
            <th>Withdrawal of consent</th>
            <td>
              At any time, through express statement, without affecting the
              lawfulness of prior processing.
            </td>
          </tr>
          <tr>
            <th>Review of automated decisions</th>
            <td>
              To request human review of decisions made solely on the basis of
              automated processing.
            </td>
          </tr>
          <tr>
            <th>Objection</th>
            <td>
              To object to processing carried out on a legal basis other than
              consent, in case of non-compliance with the LGPD.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      Requests may be made through the channel indicated in Section 12 and will
      be answered within a reasonable period, observing the ANPD's regulatory
      deadlines.
    </p>

    <h2 class="pp-section">12. Data Protection Officer (DPO)</h2>
    <p>
      The Controller (Purchaser, as per Section 2) appoints a Data Protection
      Officer, responsible for handling data subject requests, communicating
      with the ANPD and guiding staff on data protection practices.
    </p>
    <p>
      <strong>Data subject / DPO contact channel: </strong>
      <em class="pp-placeholder"
        >[to be completed by the Purchaser — DPO's name, dedicated privacy email
        and official contact channel]</em
      >
    </p>
    <p class="pp-note">
      <em
        >Field of exclusive responsibility of the Purchaser, to be completed
        before publishing this document to its data subjects — a dedicated
        privacy email, separate from the commercial email, is recommended for
        traceability of received requests.</em
      >
    </p>

    <h2 class="pp-section">13. Cookies</h2>
    <p>
      The My Life Click website and platform use cookies to enable essential
      functionality (account, login, order processing), improve the user
      experience and carry out usage analysis, including through third-party
      tools such as Google Analytics.
    </p>
    <h3 class="pp-subsection">13.1. Cookie categories</h3>
    <ul class="pp-list">
      <li>
        Strictly necessary: essential for the operation of the site and account
        security; do not require prior consent.
      </li>
      <li>
        Performance and analytics: collect aggregated data on site usage;
        require the data subject's consent.
      </li>
      <li>Functionality: remember user preferences (language, settings).</li>
      <li>
        Third-party: cookies set by analytics partners, subject to their
        respective privacy policies.
      </li>
    </ul>
    <p>
      The data subject may manage their cookie preferences at any time through
      the consent banner or browser settings. Disabling non-essential cookies
      does not affect the basic functioning of the site; disabling essential
      cookies may compromise functionality such as login and order processing.
    </p>

    <h2 class="pp-section">
      14. Data of Children, Adolescents and Persons with Disabilities
    </h2>
    <p>
      Considering that part of My Life Click's verticals is aimed at identifying
      children and persons with Autism Spectrum Disorder (PIPTEA program), the
      processing of these data subjects' data observes enhanced protection:
      collection limited to what is strictly necessary for identification and
      protection purposes, specific consent from the legal guardian, a ban on
      using data for advertising purposes directed at these categories of data
      subjects, and the possibility of deleting the data at any time upon the
      legal guardian's request.
    </p>

    <h2 class="pp-section">15. User Commitment</h2>
    <p>By using the My Life Click platform, the user agrees to:</p>
    <ul class="pp-list">
      <li>
        Not use the services for unlawful purposes or purposes contrary to
        public order.
      </li>
      <li>
        Not disseminate discriminatory, racist, xenophobic content, or content
        that promotes violence or illegal activities.
      </li>
      <li>
        Not attempt to compromise the security of the Controller's, suppliers'
        or third parties' systems, including attempts to introduce viruses,
        malware or unauthorized reverse engineering.
      </li>
      <li>
        Provide truthful, accurate and up-to-date information at the time of
        registration.
      </li>
    </ul>

    <h2 class="pp-section">16. Intellectual Property and License to Use</h2>
    <p>
      The materials made available on the My Life Click website and platform
      (text, brand, layout, software and QR Code/NFC/RFID identification
      technology) are protected by copyright and industrial property rights. The
      user is granted a limited, personal, non-exclusive and non-transferable
      license to use the services in accordance with their purpose, and copying,
      modifying, reverse engineering, distributing or unauthorized commercial
      exploitation of the materials is prohibited.
    </p>

    <h2 class="pp-section">17. Disclaimer and Limitation of Liability</h2>
    <p>
      Materials and services are provided "as is". My Life Click makes no
      warranties, express or implied, and hereby disclaims and denies all other
      warranties, and is not liable for indirect damages arising from the use or
      inability to use the services, except in cases of willful misconduct,
      gross negligence, or violation of a data protection rule attributable to
      the Controller, pursuant to the Consumer Protection Code and the LGPD.
    </p>
    <p class="pp-note">
      <em
        >Note: limitation of liability clauses against consumers are subject to
        the public-order rules of the Brazilian Consumer Protection Code, which
        prohibit the exclusion of liability in cases of service defect; legal
        validation of this section is recommended according to the target
        audience (B2C, B2B or public sector).</em
      >
    </p>

    <h2 class="pp-section">18. Links to External Sites</h2>
    <p>
      The platform may contain links to third-party websites. My Life Click does
      not review or take responsibility for the content, privacy practices or
      security of external sites, and access to such sites is the sole
      responsibility of the user.
    </p>

    <h2 class="pp-section">19. Changes to this Policy</h2>
    <p>
      This Policy may be updated at any time to reflect legislative, regulatory
      or operational changes. Relevant changes will be communicated to data
      subjects by email or through a prominent notice on the platform,
      indicating the new effective date. Continued use of the services after the
      change is communicated implies awareness of the new terms.
    </p>

    <h2 class="pp-section">20. Applicable Law and Jurisdiction</h2>
    <p>
      This Policy is governed by the laws of the Federative Republic of Brazil,
      in particular Law No. 13,709/2018 (LGPD), Law No. 12,965/2014 (Brazilian
      Civil Rights Framework for the Internet) and the Consumer Protection Code.
      The jurisdiction of the data subject's/consumer's domicile is elected to
      settle disputes arising from this Policy, except where different
      jurisdiction is provided for by law, and the choice of a jurisdiction that
      hinders the data subject's access to justice is prohibited.
    </p>

    <h2 class="pp-section">21. Effective Date</h2>
    <p>
      This Policy takes effect as of August 25, 2026 and fully replaces any
      previous version published by the Purchaser.
    </p>

    <hr class="pp-divider" />
    <p class="pp-footnote">
      <em
        >This document was prepared by My Life Click for the purpose of
        organizing and updating content, based on Brazilian data protection
        legislation (LGPD) and market best practices, as a template to be
        adapted by each Purchaser. Completing, validating and publishing the
        fields indicated as "[to be completed by the Purchaser]" is the
        exclusive responsibility of the Purchaser of My Life Click products, who
        is also responsible for having the document reviewed by a lawyer or law
        firm specialized in data protection and digital law licensed in their
        jurisdiction, adapting it to their size and sector of operation (from
        public bodies to multinational companies), and preparing a Data
        Protection Impact Assessment (DPIA), recommended given the processing of
        sensitive health data. My Life Click is not responsible for the content
        inserted by the Purchaser in these fields.</em
      >
    </p>
  </div>
</template>

<script>
export default {
  name: "privacy-policy-en",
};
</script>
